import React, { useMemo } from "react";
import { AppBar, ReversingLabsLogo, VerticalDivider } from "@sscs/ui";
import { getClassName } from "@sscs/utils";
import { FILESTREAM_ROUTE } from "Constants";
import { useGroup } from "Hooks";
import { useGetWhoamiQuery } from "Services";
import styles from "./header.module.scss";
import NavigationTabs from "./NavigationTabs/NavigationTabs";
import OrgAndGroupSelect from "./OrgAndGroupSelect/OrgAndGroupSelect";
import useLastGroupLocation from "./OrgAndGroupSelect/useLastGroupLocation";
import Organization from "./Organization/Organization";
import UserInfo from "./UserInfo/UserInfo";

const Header = (): React.JSX.Element => {
  const { data: whoami } = useGetWhoamiQuery();
  const currentGroup = useGroup();
  const lastGroupLocation = useLastGroupLocation();

  // On an org-level page the group context is gone, so the navigation tabs and
  // the group select can't be shown. Instead resolve the group to return to (the
  // last-visited one, falling back to the default/first group) and the exact page
  // within it.
  const backToGroup = useMemo(() => {
    if (!whoami || currentGroup) return null;

    const rememberedGroup = lastGroupLocation
      ? whoami.org_groups.find(
          (group) => group.group_id === lastGroupLocation.groupId,
        )
      : undefined;
    const targetGroup =
      rememberedGroup ??
      whoami.org_groups.find((group) => group.is_default) ??
      whoami.org_groups[0];

    if (!targetGroup) return null;

    return {
      name: targetGroup.name,
      path:
        rememberedGroup && lastGroupLocation
          ? lastGroupLocation.path
          : `/${FILESTREAM_ROUTE}/group/${targetGroup.group_id}`,
    };
  }, [whoami, currentGroup, lastGroupLocation]);

  return (
    <AppBar position="static" color="primary" className={styles.root}>
      <div className={styles["logo-wrapper"]}>
        <ReversingLabsLogo className={styles["nav-logo"]} />
      </div>
      <div
        className={getClassName(styles["toolbar-controls"], {
          [styles["controls-hidden"]]: Boolean(backToGroup),
        })}
      >
        <OrgAndGroupSelect backToGroup={backToGroup} />
        <VerticalDivider />
        <NavigationTabs />
      </div>

      <div className={styles["info-and-help"]}>
        <UserInfo />
        <Organization />
      </div>
    </AppBar>
  );
};

export default Header;
